import { z } from "zod/v4";

import { RuleGraphCoverageDescriptions } from "../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../descriptions/types";
import { isRuleGraphPipelineKind } from "./rule-graph";
import type { RuleGraphDto } from "./rule-graph";

// ---------------------------------------------------------------------------
// Rule-graph coverage — the join between one evaluation's branch-level
// telemetry and the rule's graph artifact. Computed server-side after an
// evaluation and consumed by the graph renderer to dim untraversed lanes;
// pure and dependency-light so both sides share one definition.
// ---------------------------------------------------------------------------

export const RuleGraphBranchStateList = [
  "fired",
  "attempted",
  "skipped",
] as const;
export const RuleGraphBranchStateSchema = z.enum(RuleGraphBranchStateList);
export type RuleGraphBranchState = (typeof RuleGraphBranchStateList)[number];

export const RuleGraphCoverageSchema = z
  .object({
    firedBranchId: z
      .string()
      .nullable()
      .describe(RuleGraphCoverageDescriptions.firedBranchId),
    branchStates: z
      .record(z.string(), RuleGraphBranchStateSchema)
      .describe(RuleGraphCoverageDescriptions.branchStates),
    traversedNodeIds: z
      .array(z.string())
      .describe(RuleGraphCoverageDescriptions.traversedNodeIds),
    terminalNodeId: z
      .string()
      .describe(RuleGraphCoverageDescriptions.terminalNodeId),
  })
  .describe(RuleGraphCoverageDescriptions._schema);
export type RuleGraphCoverage = z.infer<typeof RuleGraphCoverageSchema>;

/**
 * The slice of an evaluation result coverage needs. Structural on purpose:
 * the full envelope/telemetry schemas live under functions/agentic-logic/,
 * which imports this dto layer — naming only the fields used keeps the
 * dependency arrow pointing one way while `AgenticLogicRuleLambdaResult`
 * satisfies it verbatim.
 */
export interface RuleGraphCoverageEvaluationInput {
  envelope: {
    items: ReadonlyArray<unknown>;
    branchFired?: { base: string } | null;
  };
  telemetry: {
    attempts?: ReadonlyArray<{ base: string }> | null;
  };
  exclusions?: {
    evaluated: boolean;
  };
}

function branchState(
  branchId: string,
  firedBranchId: string | null,
  attemptedBases: ReadonlySet<string>
): RuleGraphBranchState {
  if (branchId === firedBranchId) return "fired";
  if (attemptedBases.has(branchId)) return "attempted";
  return "skipped";
}

/**
 * Join one evaluation's branch-level telemetry onto the graph.
 *
 * - A branch node is `fired` when it is the envelope's `branchFired.base`,
 *   `attempted` when it appears in `telemetry.attempts[].base` without
 *   firing, and `skipped` otherwise. Telemetry bases that name no graph
 *   node are ignored, never thrown — telemetry and graph are produced by
 *   different revisions and may briefly disagree.
 * - `traversedNodeIds` is the solid path: the input node, the fired branch
 *   and its `branchId` lane, every un-branched pipeline node (shared
 *   post-filters — only when something fired, or when the graph has no
 *   branches at all), and the terminal. An EMPTY envelope normally has no
 *   solid path through any lane. Exclusions are the exception: when the rule
 *   produced candidates and Exclusions removed all of them, the selected lane
 *   and Exclusions stay solid before the fallback terminal.
 * - The terminal is the `output` node when the envelope carries items and
 *   the `fallback-terminal` when it is empty.
 */
export function computeRuleGraphCoverage(
  graph: RuleGraphDto,
  result: RuleGraphCoverageEvaluationInput
): RuleGraphCoverage {
  const branchNodes = graph.nodes.filter((node) => node.kind === "branch");
  const branchIds = new Set(branchNodes.map((node) => node.id));

  const reportedFired = result.envelope.branchFired?.base ?? null;
  const firedBranchId =
    reportedFired !== null && branchIds.has(reportedFired)
      ? reportedFired
      : null;

  const attemptedBases = new Set(
    (result.telemetry.attempts ?? []).map((attempt) => attempt.base)
  );
  const branchStates: Record<string, RuleGraphBranchState> = {};
  for (const node of branchNodes) {
    branchStates[node.id] = branchState(node.id, firedBranchId, attemptedBases);
  }

  const hasItems = result.envelope.items.length > 0;
  const exclusionsEvaluated = result.exclusions?.evaluated === true;
  const selectionProducedItems = hasItems || exclusionsEvaluated;
  const terminalKind = hasItems ? "output" : "fallback-terminal";
  const terminalNodeId =
    graph.nodes.find((node) => node.kind === terminalKind)?.id ?? "";

  const branchless = branchNodes.length === 0;

  const traversed: string[] = [];
  for (const node of graph.nodes) {
    const isTraversed =
      node.kind === "input" ||
      node.id === terminalNodeId ||
      (node.kind === "exclusions" && selectionProducedItems) ||
      (selectionProducedItems &&
        ((node.kind === "branch" && node.id === firedBranchId) ||
          (node.kind !== "exclusions" &&
            isRuleGraphPipelineKind(node.kind) &&
            (node.branchId !== undefined
              ? node.branchId === firedBranchId
              : branchless || firedBranchId !== null))));
    if (isTraversed) traversed.push(node.id);
  }

  return {
    firedBranchId,
    branchStates,
    traversedNodeIds: traversed,
    terminalNodeId,
  };
}

assertDescriptions<typeof RuleGraphCoverageDescriptions, RuleGraphCoverage>();
